import { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Send, Shield, ArrowRight } from "lucide-react";
import axios from "axios";
import { API } from "../lib/config";
import { getAffiliate } from "../lib/useSettings";

const SARAH_IMG = "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=200&h=200&fit=crop&crop=face";

export function ChatBot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [loading, setLoading] = useState(false);
  const bottom = useRef(null);

  useEffect(() => { bottom.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", text: msg }]);
    setLoading(true);
    try {
      const r = await axios.post(`${API}/chat`, { message: msg, session_id: sessionId });
      setSessionId(r.data.session_id);
      setMessages(prev => [...prev, { role: "assistant", text: r.data.reply }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", text: "Sorry, I'm having trouble connecting. Please try again." }]);
    }
    setLoading(false);
  };

  return (
    <>
      {!open && (
        <button onClick={() => { setOpen(true); if (!messages.length) setMessages([{ role: "assistant", text: "Hi! I'm Sarah, your AI auto insurance guide. Ask me anything about coverage types, state requirements, or how to save on your premium! I'm not an agent." }]); }} className="fixed bottom-20 right-6 z-40 group" data-testid="chat-toggle">
          <div className="relative">
            <div className="absolute inset-0 bg-sky-400 rounded-full animate-ping opacity-30" />
            <img src={SARAH_IMG} alt="Chat with Sarah" className="w-14 h-14 rounded-full object-cover border-3 border-white shadow-lg relative z-10 group-hover:scale-110 transition-transform" />
          </div>
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white z-20" />
        </button>
      )}
      {open && (
        <div className="fixed bottom-20 right-6 z-50 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden" style={{ maxHeight: "480px" }} data-testid="chat-window">
          <div className="bg-sky-700 text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src={SARAH_IMG} alt="Sarah" className="w-8 h-8 rounded-full object-cover" />
              <div>
                <p className="text-sm font-semibold">Sarah</p>
                <p className="text-[10px] text-sky-200">AI Auto Insurance Guide</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-sky-600 rounded" data-testid="chat-close"><X size={18} /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" style={{ minHeight: "200px" }}>
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${m.role === "user" ? "bg-sky-700 text-white" : "bg-slate-100 text-slate-700"}`}>{m.text}</div>
              </div>
            ))}
            {loading && <div className="flex justify-start"><div className="bg-slate-100 rounded-xl px-3 py-2 text-sm text-slate-400">Typing...</div></div>}
            <div ref={bottom} />
          </div>
          <div className="border-t border-slate-200 px-3 py-2 flex gap-2">
            <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") send(); }} placeholder="Ask about auto insurance..." className="flex-1 text-sm px-3 py-2 border border-slate-200 rounded-lg focus:ring-1 focus:ring-sky-500 outline-none" data-testid="chat-input" />
            <button onClick={send} disabled={loading} className="bg-sky-700 text-white rounded-lg p-2 hover:bg-sky-800 disabled:opacity-50" data-testid="chat-send"><Send size={16} /></button>
          </div>
          <p className="text-[9px] text-slate-400 text-center pb-2">Sarah is an AI assistant. For personalized advice, consult a licensed agent.</p>
        </div>
      )}
    </>
  );
}

export function ExitIntentPopup() {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const lastScrollY = useRef(0);
  const triggered = useRef(false);

  const dismiss = useCallback(() => {
    setShow(false);
    setDismissed(true);
    sessionStorage.setItem("qbc_exit_dismissed", "1");
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem("qbc_exit_dismissed")) return;

    // Desktop: mouse leaving viewport at top
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !triggered.current && !dismissed) {
        triggered.current = true;
        setShow(true);
      }
    };

    // Mobile: user scrolls up significantly (going to leave)
    const handleScroll = () => {
      const currentY = window.scrollY;
      if (currentY < lastScrollY.current - 150 && currentY > 200 && !triggered.current && !dismissed) {
        triggered.current = true;
        setShow(true);
      }
      lastScrollY.current = currentY;
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      document.removeEventListener("mouseleave", handleMouseLeave);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [dismissed]);

  if (!show) return null;

  const scrollToQuiz = () => {
    dismiss();
    const el = document.getElementById("quiz");
    if (el) el.scrollIntoView({ behavior: "smooth" });
    else window.location.href = "/#quiz";
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="exit-intent-popup">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={dismiss} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 text-center animate-[slideUp_0.3s_ease-out]">
        <button onClick={dismiss} className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 p-1" data-testid="exit-dismiss"><X size={20} /></button>
        <img src={SARAH_IMG} alt="Sarah" className="w-16 h-16 rounded-full object-cover mx-auto mb-3 border-2 border-sky-200" />
        <h3 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Wait! Did you find what you needed?</h3>
        <p className="text-sm text-slate-500 mb-5">Before you go, try our Good Driver Quiz to see if you qualify for discounts — or compare rates in your area.</p>
        <div className="space-y-2">
          <button onClick={scrollToQuiz} className="w-full bg-sky-700 text-white rounded-xl px-5 py-3 text-sm font-medium hover:bg-sky-800 transition-all flex items-center justify-center gap-2" data-testid="exit-quiz-btn">
            <Shield size={16} /> Take the Good Driver Quiz
          </button>
          <a href="https://www.allstate.com/auto-insurance" target="_blank" rel="noopener noreferrer" onClick={dismiss} className="w-full bg-amber-500 text-white rounded-xl px-5 py-3 text-sm font-medium hover:bg-amber-600 transition-all flex items-center justify-center gap-2 block" data-testid="exit-quote-btn">
            Compare Rates Now <ArrowRight size={14} />
          </a>
          <button onClick={dismiss} className="text-xs text-slate-400 hover:text-slate-600 mt-2" data-testid="exit-no-thanks">No thanks, I'm good</button>
        </div>
      </div>
    </div>
  );
}
